import React, { Fragment } from 'react';
import { Transition, Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Link } from '../util/router';
import SeenByAvatarChip from './SeenByAvatarChip';

/**
 * Full list of users who saw a show — tap a row to open their profile.
 */
function SeenByModal({ isOpen, onClose, seenByUsers = [] }) {
  const count = seenByUsers.length;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto p-4 flex items-center justify-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-0 shadow-xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                <Dialog.Title className="text-lg font-semibold text-gray-900 font-oswald">
                  Seen by {count} {count === 1 ? 'person' : 'people'}
                </Dialog.Title>
                <button
                  type="button"
                  className="rounded-full p-1 text-gray-500 hover:bg-gray-100"
                  onClick={onClose}
                  aria-label="Close"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <ul className="max-h-[min(24rem,70vh)] overflow-y-auto py-2">
                {seenByUsers.map(user => {
                  const row = (
                    <>
                      <div className="shrink-0">
                        <SeenByAvatarChip user={user} sizeClass="w-10 h-10" />
                      </div>
                      <div className="min-w-0 flex-1 text-left">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          @{user.username || 'unknown'}
                        </p>
                        {user.name && <p className="text-xs text-gray-500 truncate">{user.name}</p>}
                      </div>
                    </>
                  );
                  return (
                    <li key={user.id}>
                      {user.username ? (
                        <Link
                          to={`/profile/${encodeURIComponent(user.username)}`}
                          onClick={onClose}
                          className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors"
                        >
                          {row}
                        </Link>
                      ) : (
                        <div className="flex items-center gap-3 px-4 py-2.5 opacity-80">{row}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

export default SeenByModal;
